import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Autocomplete,
    TextField,
    Button,
    Stack
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'contexts/AuthContext';
import { DateTime } from 'luxon';
import humanizeDuration from 'humanize-duration';
import { formatDate } from 'util/helper';
import { enqueueSnackbar } from 'notistack';
import { useJourney } from '../../../../api/Journeys/journey_queries';

const JourneySetupSidebar = ({ hideSidebar }) => {
    const { id: journeyIdStr } = useParams();
    const journeyId = parseInt(journeyIdStr);
    
    const { data: journey, isSuccess } = useJourney(journeyId);
    const { axiosElementalApiCurrentOrg } = useAuth();
    const queryClient = useQueryClient();
    
    const [optizmoSuppressionListId, setOptizmoSuppressionListId] = useState(
        journey?.optizmo_suppression_list_id ?? null
    );

    // Update local state when journey data changes
    useEffect(() => {
        if (journey) {
            setOptizmoSuppressionListId(journey.optizmo_suppression_list_id ?? null);
        }
    }, [journey]);

    // Query for optizmo suppression lists
    const optizmoListsQuery = useQuery({
        queryKey: ['suppression-lists-optizmo'],
        queryFn: () => {
            return axiosElementalApiCurrentOrg.get('/settings/suppression-lists/optizmo');
        },
        select: (data) => {
            // Filter out those that haven't been synced recently
            const synced_recently = data?.filter(item => 
                DateTime.fromISO(item.last_sync_at) > DateTime.now().minus({ weeks: 1 })
            ) ?? [];

            synced_recently.sort((a, b) => (a.campaign_name ?? '').localeCompare(b.campaign_name ?? ''));
            return synced_recently;
        }
    });

    // Mutation to update journey optizmo suppression list
    const mutationUpdateJourneySetup = useMutation({
        mutationFn: async (data) => {
            return await axiosElementalApiCurrentOrg.put(`/flows/${journeyId}`, data);
        },
        onSuccess: (data) => {
            // Update the journey data with the new optizmo suppression list
            queryClient.setQueryData(['journeys', journeyId], data);
        },
        onError: (error) => {
            console.error('Failed to update journey setup:', error);
        }
    });

    // Handle success and error with useEffect as per repo rules
    useEffect(() => {
        if (mutationUpdateJourneySetup.isSuccess) {
            enqueueSnackbar('Journey setup updated successfully', { variant: 'success' });
            // Invalidate journey data to refresh
            queryClient.invalidateQueries(['journeys', journeyId]);
        }
    }, [mutationUpdateJourneySetup.isSuccess, queryClient, journeyId]);

    useEffect(() => {
        if (mutationUpdateJourneySetup.isError) {
            enqueueSnackbar('Failed to update journey setup', { variant: 'error' });
        }
    }, [mutationUpdateJourneySetup.isError]);

    // Handle optizmo suppression list change
    const handleOptizmoSuppressionListChange = (event, newValue) => {
        const newId = newValue ? newValue.id : null;
        setOptizmoSuppressionListId(newId);
    };

    // Handle submit
    const handleSubmit = () => {
        // Only update if the value has actually changed
        if (optizmoSuppressionListId !== journey?.optizmo_suppression_list_id) {
            mutationUpdateJourneySetup.mutate({
                optizmo_suppression_list_id: optizmoSuppressionListId
            });
        }
    };

    if (!isSuccess || !journey) {
        return (
            <div className='flow-sidebar' style={{width: '15vw'}}>
                <Box className='flow-sidebar__header'>
                    <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                        Journey Setup
                    </Typography>
                </Box>
                <Box className='flow-sidebar__body'>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
                        Loading journey data...
                    </Typography>
                </Box>
            </div>
        );
    }

    // Loading state
    if (optizmoListsQuery.isLoading) {
        return (
            <div className='flow-sidebar' style={{width: '15vw'}}>
                <Box className='flow-sidebar__header'>
                    <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                        Journey Setup
                    </Typography>
                </Box>
                <Box className='flow-sidebar__body'>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
                        Loading setup options...
                    </Typography>
                </Box>
            </div>
        );
    }

    // Error state
    if (optizmoListsQuery.isError) {
        return (
            <div className='flow-sidebar' style={{width: '15vw'}}>
                <Box className='flow-sidebar__header'>
                    <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                        Journey Setup
                    </Typography>
                </Box>
                <Box className='flow-sidebar__body'>
                    <Typography variant="body2" color="error" align="center" sx={{ p: 2 }}>
                        Error loading setup options
                    </Typography>
                </Box>
            </div>
        );
    }

    return (
        <div className='flow-sidebar' style={{width: '15vw'}}>
            <Box className='flow-sidebar__header'>
                <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                    Journey Setup
                </Typography>
            </Box>
            <Box className='flow-sidebar__body'>
                <Box sx={{ p: 2 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Offer Suppression
                            </Typography>
                            <Autocomplete
                                id="journey-optizmo-list-id"
                                options={[
                                    { campaign_name: 'None', id: null }, 
                                    ...optizmoListsQuery.data
                                ]}
                                getOptionLabel={(option) => {
                                    let label = `${option.campaign_name}`;
                                    let syncLabel = null;
                                    const lastSyncAt = option.last_sync_at;
                                    
                                    if (!lastSyncAt) {
                                        if (option.campaign_name !== 'None') {
                                            syncLabel = 'never';
                                        }
                                    } else {
                                        const offset = DateTime.fromISO(lastSyncAt).diffNow().negate();
                                        syncLabel = humanizeDuration(offset.toMillis(), {
                                            units: ['d', 'h'],
                                            largest: 1,
                                            round: true
                                        }) + ' ago';
                                    }

                                    if (option.created_at) {
                                        label += ` [created: ${formatDate(option.created_at)}]`;
                                    }

                                    if (syncLabel) {
                                        label += ` [synced: ${syncLabel}]`;
                                    }
                                    return label;
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params}
                                        fullWidth
                                        size="small"
                                        placeholder="Select suppression list..."
                                    />
                                )}
                                value={optizmoListsQuery.data?.find(option => 
                                    option.id === optizmoSuppressionListId
                                ) || { campaign_name: 'None', id: null }}
                                onChange={handleOptizmoSuppressionListChange}
                                size="small"
                                sx={{ width: '100%' }}
                                loading={optizmoListsQuery.isLoading}
                                loadingText="Loading suppression lists..."
                                noOptionsText="No suppression lists available"
                            />
                        </Box>
                        
                        {/* Submit Button */}
                        <Box display="flex" justifyContent="space-between" mt={2}>
                            <Button
                                variant="outlined"
                                onClick={hideSidebar}
                                size="small"
                            >
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={mutationUpdateJourneySetup.isLoading || optizmoSuppressionListId === journey?.optizmo_suppression_list_id}
                                size="small"
                            >
                                {mutationUpdateJourneySetup.isLoading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

export default JourneySetupSidebar;
